import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { Detective, MagnifyingGlass } from "@phosphor-icons/react";

export default function AdminAuditWo() {
  const [woNum, setWoNum] = useState("2548-1");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [routerBusy, setRouterBusy] = useState(false);
  const [routerResult, setRouterResult] = useState(null);
  const [mapBusy, setMapBusy] = useState(false);
  const [mapResult, setMapResult] = useState(null);
  const [caseBBusy, setCaseBBusy] = useState(false);
  const [caseBResult, setCaseBResult] = useState(null);
  const [rematchBusy, setRematchBusy] = useState(false);
  const [rematchResult, setRematchResult] = useState(null);

  const runRematch = async (dry_run) => {
    if (!woNum.trim()) return;
    if (!dry_run && !window.confirm("Re-check every photo on this WO with the strict matcher and MOVE misrouted ones (to their real WO, or to UNSORTED-PHOTOS). Continue?")) return;
    setRematchBusy(true);
    try {
      const r = await api.post("/admin/photo-router/rematch", { source: woNum.trim(), dry_run });
      setRematchResult(r.data);
      const s = r.data.summary || {};
      toast.success(dry_run
        ? `Would move ${(s.to_other || 0) + (s.to_unsorted || 0)} · keep ${s.keep || 0}`
        : `Moved ${r.data.moved} photos · purged ${r.data.cache_rows_purged} cache rows`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Re-match failed"));
    } finally { setRematchBusy(false); }
  };

  const run = async () => {
    if (!woNum.trim()) return;
    setBusy(true); setResult(null); setRouterResult(null); setCaseBResult(null);
    try {
      const r = await api.get(`/admin/routing-audit/${encodeURIComponent(woNum.trim())}?limit=8`);
      setResult(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Audit failed"));
    } finally { setBusy(false); }
  };

  const runRouter = async (dry_run) => {
    if (!woNum.trim()) return;
    if (!dry_run) {
      if (!window.confirm("This will MOVE photos off the source WO onto their matching FR WOs. Continue?")) return;
    }
    setRouterBusy(true);
    try {
      const r = await api.post("/admin/photo-router/run",
        { source: woNum.trim(), dry_run });
      setRouterResult(r.data);
      toast.success(dry_run ? "Dry-run complete" : `Moved ${r.data.moved} photos`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Router failed"));
    } finally { setRouterBusy(false); }
  };

  const buildMap = async (force_refresh = false) => {
    if (!woNum.trim()) return;
    setMapBusy(true);
    try {
      const r = await api.post("/admin/case-b/build-map",
        { source_wo: woNum.trim(), force_refresh });
      setMapResult(r.data);
      const s = r.data.stats || {};
      toast.success(`Fetched ${s.task_details_fetched || 0} tasks · +${s.attachments_indexed || 0} attachments`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Build map failed"));
    } finally { setMapBusy(false); }
  };

  const fillWorkspace = async () => {
    setMapBusy(true);
    try {
      const r = await api.post("/admin/case-b/build-map", { tasks_per_list: 500 });
      setMapResult(r.data);
      const s = r.data.stats || {};
      toast.success(`Fetched ${s.task_details_fetched || 0} tasks · +${s.attachments_indexed || 0} attachments`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Fill workspace failed"));
    } finally { setMapBusy(false); }
  };

  const createMissingWos = async (dry_run) => {
    if (!woNum.trim()) return;
    if (!dry_run) {
      if (!window.confirm("This will CREATE new FR WOs for every ClickUp task whose WO# doesn't exist yet. Continue?")) return;
    }
    setMapBusy(true);
    try {
      const r = await api.post("/admin/case-b/create-missing-wos",
        { source_wo: woNum.trim(), dry_run });
      setMapResult(r.data);
      if (dry_run) {
        toast.success(`Would create ${r.data.would_create_wo_count} WOs from ${r.data.task_count} tasks`);
      } else {
        toast.success(`Created ${r.data.wo_created} WOs · remapped ${r.data.task_cache_remapped} tasks`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Create WOs failed"));
    } finally { setMapBusy(false); }
  };

  const runCaseB = async (dry_run) => {
    if (!woNum.trim()) return;
    if (!dry_run) {
      if (!window.confirm("This will MOVE invoices/emails off the source WO onto their matching FR WOs. Continue?")) return;
    }
    setCaseBBusy(true);
    try {
      const r = await api.post("/admin/case-b/run",
        { source: woNum.trim(), dry_run });
      setCaseBResult(r.data);
      toast.success(dry_run ? "Dry-run complete" : `Moved ${r.data.moved} notes`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "CASE_B failed"));
    } finally { setCaseBBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-8 max-w-5xl">
        <div className="flex items-center gap-2">
          <Detective weight="fill" size={26} className="text-fuchsia-600"/>
          <h1 className="font-display font-black text-3xl">Routing Audit</h1>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Diagnostic tool. Enter a WO# → we sample its attachments and tell you exactly which routing metadata is present so we know what auto-router to build. Zero data changes.
        </p>
        <div className="mt-4 flex gap-2 flex-wrap">
          <input value={woNum} onChange={(e) => setWoNum(e.target.value)}
            placeholder="WO number (e.g. 2548-1)"
            data-testid="audit-wo-input"
            className="h-11 px-3 border-2 border-slate-900 font-mono font-black text-lg w-64"/>
          <button onClick={run} disabled={busy || !woNum.trim()}
            data-testid="audit-wo-run"
            className="h-11 px-5 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-2 shadow-[3px_3px_0_#0f172a]">
            <MagnifyingGlass weight="bold" size={14}/>
            {busy ? "Running…" : "Run Audit"}
          </button>
        </div>

        {result && (
          <div className="mt-6 space-y-4">
            <div className={`p-4 border-l-4 rounded ${
              result.verdict?.startsWith("CASE_A") ? "border-emerald-500 bg-emerald-50" :
              result.verdict?.startsWith("CASE_B") ? "border-orange-500 bg-orange-50" :
              "border-red-500 bg-red-50"}`}>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Verdict</div>
              <div className="text-lg font-black" data-testid="audit-verdict">{result.verdict}</div>
              <div className="text-xs text-slate-600 mt-1">
                WO {result.wo} · mongo id <span className="font-mono">{result.wo_id}</span> · property {result.wo_property || "—"}
              </div>
            </div>

            {result.verdict?.startsWith("CASE_A") && (
              <div className="p-4 border-2 border-emerald-500 bg-emerald-50 rounded space-y-2">
                <div className="font-black uppercase tracking-widest text-emerald-900 text-sm">Photo Router · CASE_A ready</div>
                <div className="text-xs text-slate-700">
                  Every after_photo carries its own <code className="bg-slate-100 px-1 py-0.5 text-[10px]">clickup_task_id</code>.
                  Preview which target FR WOs the photos will land on, then execute.
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => runRouter(true)} disabled={routerBusy}
                    data-testid="router-dry-run"
                    className="h-10 px-4 border-2 border-emerald-600 bg-white text-emerald-800 hover:bg-emerald-100 font-black uppercase text-xs tracking-widest rounded">
                    {routerBusy ? "Running…" : "Dry Run (preview)"}
                  </button>
                  <button onClick={() => runRouter(false)} disabled={routerBusy || !routerResult}
                    data-testid="router-execute"
                    className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a]">
                    {routerBusy ? "Moving…" : "Execute Move"}
                  </button>
                </div>
                {routerResult && (
                  <pre className="bg-slate-900 text-emerald-300 p-3 rounded text-xs overflow-x-auto font-mono max-h-96 overflow-y-auto"
                    data-testid="router-result">
{JSON.stringify(routerResult, null, 2)}
                  </pre>
                )}
              </div>
            )}

            {/* Strict re-match — for catch-all WOs like 3242-1 */}
            <div className="p-4 border-2 border-red-500 bg-red-50 rounded space-y-2">
              <div className="font-black uppercase tracking-widest text-red-900 text-sm">Strict Re-check · catch-all cleanup</div>
              <div className="text-xs text-slate-700">
                Re-runs every photo on this WO through the strict WO# matcher (exact number, optional letter prefix — no more “ends with” matches).
                Photos that no longer belong here move to their real WO, or to <code className="bg-slate-100 px-1 py-0.5 text-[10px]">UNSORTED-PHOTOS</code> if nothing matches. Poisoned cache rows pointing here get purged.
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => runRematch(true)} disabled={rematchBusy}
                  data-testid="rematch-dry-run"
                  className="h-10 px-4 border-2 border-red-600 bg-white text-red-800 hover:bg-red-100 font-black uppercase text-xs tracking-widest rounded">
                  {rematchBusy ? "Checking…" : "Preview Re-check"}
                </button>
                <button onClick={() => runRematch(false)} disabled={rematchBusy || !rematchResult}
                  data-testid="rematch-execute"
                  className="h-10 px-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a]">
                  {rematchBusy ? "Moving…" : "Execute Re-check"}
                </button>
              </div>
              {rematchResult && (
                <pre className="bg-slate-900 text-red-300 p-3 rounded text-xs overflow-x-auto font-mono max-h-96 overflow-y-auto"
                  data-testid="rematch-result">
{JSON.stringify(rematchResult, null, 2)}
                </pre>
              )}
            </div>

            {/* CASE_B — always shown; independent from photo router */}
            <div className="p-4 border-2 border-orange-500 bg-orange-50 rounded space-y-2">
              <div className="font-black uppercase tracking-widest text-orange-900 text-sm">CASE_B Router · invoices / emails / PDFs</div>
              <div className="text-xs text-slate-700">
                Note-level ClickUp attachments (no task id on the note itself). First build the <code className="bg-slate-100 px-1 py-0.5 text-[10px]">attachment_id → task_id</code> map by scanning your watched ClickUp lists, then dry-run to preview + execute.
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => buildMap(false)} disabled={mapBusy}
                  data-testid="case-b-build-map"
                  className="h-10 px-4 border-2 border-orange-600 bg-white text-orange-800 hover:bg-orange-100 font-black uppercase text-xs tracking-widest rounded">
                  {mapBusy ? "Scanning…" : "1 · Build Attachment Map"}
                </button>
                <button onClick={() => buildMap(true)} disabled={mapBusy}
                  data-testid="case-b-force-rescan"
                  title="Re-fetch every task ignoring the scan checkpoint. Use once after a code fix or when the map looks incomplete."
                  className="h-10 px-3 border-2 border-orange-400 bg-white text-orange-700 hover:bg-orange-100 font-black uppercase text-[10px] tracking-widest rounded">
                  {mapBusy ? "…" : "↻ Force Rescan"}
                </button>
                <button onClick={fillWorkspace} disabled={mapBusy}
                  data-testid="case-b-fill-workspace"
                  title="Scan remaining tasks in your watched ClickUp lists (skips already-scanned). Use if map_hits is low after Build Map."
                  className="h-10 px-3 border-2 border-orange-400 bg-white text-orange-700 hover:bg-orange-100 font-black uppercase text-[10px] tracking-widest rounded">
                  {mapBusy ? "…" : "↳ Fill Workspace"}
                </button>
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-orange-200">
                <div className="text-[10px] text-orange-900 font-black uppercase tracking-widest w-full">Missing WOs (if photos land back on 2548-1)</div>
                <button onClick={() => createMissingWos(true)} disabled={mapBusy}
                  data-testid="case-b-create-missing-dry"
                  title="Preview: how many new FR WOs would be created for ClickUp tasks whose WO# doesn't exist in Field Radar yet."
                  className="h-10 px-3 border-2 border-orange-600 bg-white text-orange-800 hover:bg-orange-100 font-black uppercase text-[10px] tracking-widest rounded">
                  {mapBusy ? "…" : "🔍 Preview Create WOs"}
                </button>
                <button onClick={() => createMissingWos(false)} disabled={mapBusy}
                  data-testid="case-b-create-missing-execute"
                  title="Create missing FR WOs and remap the task cache so Photo Router / CASE_B can route to them."
                  className="h-10 px-3 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase text-[10px] tracking-widest rounded shadow-[3px_3px_0_#0f172a]">
                  {mapBusy ? "…" : "➕ Create Missing WOs"}
                </button>
                <button onClick={() => runCaseB(true)} disabled={caseBBusy}
                  data-testid="case-b-dry-run"
                  className="h-10 px-4 border-2 border-orange-600 bg-white text-orange-800 hover:bg-orange-100 font-black uppercase text-xs tracking-widest rounded">
                  {caseBBusy ? "Running…" : "2 · Dry Run"}
                </button>
                <button onClick={() => runCaseB(false)} disabled={caseBBusy || !caseBResult}
                  data-testid="case-b-execute"
                  className="h-10 px-4 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a]">
                  {caseBBusy ? "Moving…" : "3 · Execute Move"}
                </button>
              </div>
              {mapResult && (
                <pre className="bg-slate-900 text-orange-300 p-3 rounded text-xs overflow-x-auto font-mono max-h-56 overflow-y-auto"
                  data-testid="case-b-map-result">
{JSON.stringify(mapResult, null, 2)}
                </pre>
              )}
              {caseBResult && (
                <pre className="bg-slate-900 text-orange-300 p-3 rounded text-xs overflow-x-auto font-mono max-h-96 overflow-y-auto"
                  data-testid="case-b-result">
{JSON.stringify(caseBResult, null, 2)}
                </pre>
              )}
            </div>

            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Counts</div>
              <pre className="bg-slate-900 text-emerald-400 p-3 rounded text-xs overflow-x-auto font-mono">
{JSON.stringify(result.counts, null, 2)}
              </pre>
            </div>

            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Sample Attachments (full metadata)</div>
              <pre className="bg-slate-900 text-cyan-300 p-3 rounded text-xs overflow-x-auto font-mono max-h-[500px] overflow-y-auto">
{JSON.stringify(result.samples, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
